import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertTitle, Box, Button, IconButton, Paper, Portal, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Close, HelpOutline } from '@mui/icons-material';
import { isGuideHidden } from './useTour';

const SPOT_PADDING = 8;
const CARD_WIDTH = 360;
const GAP = 14;

// Step-by-step guided tour. Each step highlights the element matching `target`
// (a CSS selector, usually [data-tour="..."]) with an explanation card beside it.
// Steps without a target, or whose target isn't on the page, show as a centred card.
// step: { target?, title, body, action?: { label, to } }
const GuidedTour = ({ steps, open, onClose }) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  // Showing the "don't show this guide again?" warning
  const [confirmOff, setConfirmOff] = useState(false);
  // Already turned off (e.g. replayed from the Guide button): no need to offer it
  const guideAlreadyOff = Boolean(open && onClose?.guideKey && isGuideHidden(onClose.guideKey));
  const [rect, setRect] = useState(null);
  const [cardHeight, setCardHeight] = useState(0);
  const cardRef = useRef(null);
  const nextRef = useRef(null);

  const step = steps[index];
  const isLast = index === steps.length - 1;

  useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  useEffect(() => {
    setConfirmOff(false);
  }, [open, index]);

  // `target` may be one selector or a list tried in order (preferred first, then fallbacks)
  const findTarget = useCallback(() => {
    if (!step?.target) return null;
    const selectors = Array.isArray(step.target) ? step.target : [step.target];
    for (const selector of selectors) {
      const el = document.querySelector(selector);
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width && r.height) return el;
      }
    }
    return null;
  }, [step]);

  const measure = useCallback(() => {
    const el = findTarget();
    setRect(el ? el.getBoundingClientRect() : null);
  }, [findTarget]);

  // Prepare the page if the step needs it (e.g. expand a collapsed card), then
  // bring the highlighted element into view and measure it
  useLayoutEffect(() => {
    if (!open) return undefined;
    let changedPage = false;
    if (step?.prepare) {
      try {
        changedPage = Boolean(step.prepare());
      } catch {
        changedPage = false;
      }
    }
    const focus = () => {
      const el = findTarget();
      if (el) {
        // Bring the section to just below the app bar: this leaves the most room
        // underneath for the explanation card. (Sections near the end of the page
        // can't scroll that far; the card then goes above them.)
        el.scrollIntoView({ block: 'start', inline: 'nearest' });
        window.scrollBy(0, -88);
      }
      measure();
    };
    // If the step changed the page (e.g. expanded a card), the layout keeps moving
    // while the animation runs: focus once it's mostly done, then again when settled.
    const timers = [];
    if (changedPage) {
      setRect(null);
      timers.push(setTimeout(focus, 450), setTimeout(focus, 900), setTimeout(measure, 1250));
    } else {
      focus();
      timers.push(setTimeout(measure, 350)); // after scrolling settles
    }
    return () => timers.forEach(clearTimeout);
  }, [open, index, step, findTarget, measure]);

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, measure]);

  // Card height decides whether it fits below the highlight; re-measure when content changes
  useLayoutEffect(() => {
    if (cardRef.current) setCardHeight(cardRef.current.offsetHeight);
  }, [open, index, rect, isPhone, confirmOff]);

  useEffect(() => {
    if (open) nextRef.current?.focus({ preventScroll: true });
  }, [open, index]);

  // Reset to the first step on close so a replay never flashes the old step
  const finish = useCallback(() => { setIndex(0); onClose('finish'); }, [onClose]);
  const skip = useCallback(() => { setIndex(0); onClose('skip'); }, [onClose]);
  const next = useCallback(() => (isLast ? finish() : setIndex((i) => i + 1)), [isLast, finish]);
  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const neverShowAgain = useCallback(() => { setIndex(0); onClose('never'); }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (confirmOff) setConfirmOff(false);
        else skip();
      }
      if (confirmOff) return; // arrows don't change steps while the warning is open
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') back();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, next, back, skip, confirmOff]);

  if (!open || !step) return null;

  // ---- Positioning ----
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const spot = rect && {
    top: rect.top - SPOT_PADDING,
    left: rect.left - SPOT_PADDING,
    width: rect.width + SPOT_PADDING * 2,
    height: rect.height + SPOT_PADDING * 2,
  };

  let cardStyle;
  if (isPhone) {
    // Phones: dock the card at the edge that doesn't cover the highlight. If neither
    // edge is clear (very tall section), dock at the bottom so the section's top stays visible.
    const ch = cardHeight || 280;
    const fitsBelow = !spot || spot.top + spot.height + 12 + ch <= vh;
    const fitsAbove = spot && spot.top - 12 - ch >= 0;
    const dockTop = !fitsBelow && fitsAbove;
    cardStyle = { left: 12, right: 12, ...(dockTop ? { top: 'calc(12px + env(safe-area-inset-top))' } : { bottom: 'calc(12px + env(safe-area-inset-bottom))' }) };
  } else if (!spot) {
    cardStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: CARD_WIDTH + 60 };
  } else {
    // Try below, then above, then beside the highlight; never cover it if there's room
    const width = Math.min(CARD_WIDTH, vw - 32);
    const visibleTop = Math.max(spot.top, 0);
    const visibleBottom = Math.min(spot.top + spot.height, vh);
    const clampTop = (t) => Math.min(Math.max(16, t), vh - cardHeight - 16);
    const centredLeft = Math.min(Math.max(16, spot.left + spot.width / 2 - width / 2), vw - width - 16);

    if (visibleBottom + GAP + cardHeight <= vh - 16) {
      cardStyle = { top: visibleBottom + GAP, left: centredLeft, width };
    } else if (visibleTop - GAP - cardHeight >= 16) {
      cardStyle = { top: visibleTop - GAP - cardHeight, left: centredLeft, width };
    } else if (spot.left + spot.width + GAP + width <= vw - 16) {
      cardStyle = { top: clampTop(visibleTop), left: spot.left + spot.width + GAP, width };
    } else if (spot.left - GAP - width >= 16) {
      cardStyle = { top: clampTop(visibleTop), left: spot.left - GAP - width, width };
    } else {
      cardStyle = { bottom: 16, right: 16, width };
    }
  }

  const titleId = 'guided-tour-title';

  return (
    <Portal>
      {/* Click shield: keeps the page still while the tour is open */}
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{ position: 'fixed', inset: 0, zIndex: 3100, bgcolor: spot ? 'transparent' : 'rgba(15, 23, 42, 0.55)' }}
      />
      {spot && (
        <Box
          aria-hidden="true"
          sx={{
            position: 'fixed',
            zIndex: 3101,
            top: spot.top,
            left: spot.left,
            width: spot.width,
            height: spot.height,
            borderRadius: 2.5,
            boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.55), 0 0 0 3px rgba(37, 99, 235, 0.9)',
            pointerEvents: 'none',
            transition: 'top .25s ease, left .25s ease, width .25s ease, height .25s ease',
          }}
        />
      )}

      <Paper
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        elevation={8}
        sx={{
          position: 'fixed',
          zIndex: 3102,
          p: 2.5,
          borderRadius: 3,
          maxWidth: 'calc(100vw - 24px)',
          maxHeight: '70vh',
          overflowY: 'auto',
          transition: 'top .25s ease, left .25s ease',
          ...cardStyle,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="overline" sx={{ color: 'primary.main', lineHeight: 1.6 }}>
              Step {index + 1} of {steps.length}
            </Typography>
            <Typography id={titleId} variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {step.title}
            </Typography>
          </Box>
          <IconButton size="small" onClick={skip} aria-label="Close guide" sx={{ mt: -0.5, mr: -0.5 }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ color: 'text.secondary', fontSize: '0.9375rem', lineHeight: 1.6, '& p': { m: 0 }, '& p + p, & ul, & ul + p': { mt: 1 }, '& ul': { pl: 2.5, mb: 0 }, '& li + li': { mt: 0.5 }, '& b': { color: 'text.primary', fontWeight: 600 } }}>
          {step.body}
        </Box>

        {/* Progress */}
        <Box sx={{ display: 'flex', gap: 0.75, mt: 2 }} aria-hidden="true">
          {steps.map((s, i) => (
            <Box key={s.title} sx={{ height: 4, flexGrow: 1, borderRadius: 2, bgcolor: i <= index ? 'primary.main' : 'divider', transition: 'background-color .2s' }} />
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          {!isLast && (
            <Button size="small" onClick={skip} sx={{ color: 'text.secondary', mr: 'auto', px: 1 }}>
              Skip tour
            </Button>
          )}
          {isLast && <Box sx={{ mr: 'auto' }} />}
          {index > 0 && (
            <Button size="small" variant="outlined" onClick={back}>Back</Button>
          )}
          {isLast && step.action ? (
            <>
              <Button size="small" variant="outlined" onClick={finish}>Done</Button>
              <Button size="small" variant="contained" onClick={() => { finish(); navigate(step.action.to); }}>
                {step.action.label}
              </Button>
            </>
          ) : (
            <Button ref={nextRef} size="small" variant="contained" onClick={next}>
              {isLast ? 'Done' : 'Next'}
            </Button>
          )}
        </Box>

        {/* Turn off only this page's guide (it still opens from the Guide button) */}
        {!guideAlreadyOff && !confirmOff && (
          <Box sx={{ mt: 1.5, pt: 1, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
            <Button size="small" onClick={() => setConfirmOff(true)} sx={{ color: 'text.secondary', fontWeight: 500, textTransform: 'none' }}>
              Don't show this guide again
            </Button>
          </Box>
        )}
        {confirmOff && (
          <Alert severity="warning" role="alertdialog" aria-labelledby="guide-off-title" sx={{ mt: 2, '& .MuiAlert-message': { width: '100%' } }}>
            <AlertTitle id="guide-off-title" sx={{ fontWeight: 700 }}>Don't show this guide again?</AlertTitle>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              This guide won't open by itself on this page any more (on this device). Guides on other pages are not affected.
            </Typography>
            <Typography variant="body2">
              You can still see it any time by clicking the <b>Guide</b> button
              (<HelpOutline sx={{ fontSize: 16, verticalAlign: 'text-bottom' }} aria-label="question mark" /> on phones) at the top of this page.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
              <Button size="small" variant="outlined" color="inherit" onClick={() => setConfirmOff(false)} autoFocus>
                Keep showing
              </Button>
              <Button size="small" variant="contained" color="warning" onClick={neverShowAgain}>
                Don't show again
              </Button>
            </Box>
          </Alert>
        )}
      </Paper>
    </Portal>
  );
};

export default GuidedTour;
